import React from "react";
import { 
  Grid, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  IconButton, 
  Box, 
  Typography, 
  List, 
  ListItem, 
  Collapse,
  Fab,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { Link } from "react-router-dom";
import { 
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import Terms from "./parts/terms";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faTwitter } from "@fortawesome/free-brands-svg-icons";

const Footer = (props) => {
  const [showTerms, setShowTerms] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const [showScrollTop, setShowScrollTop] = React.useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Handle scroll to show scroll-to-top button on mobile
  React.useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowScrollTop(scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const closeTerms = () => {
    setShowTerms(false);
  };

  const openTerms = () => {
    setShowTerms(true);
  };

  // Desktop footer content (original styling)
  const desktopFooterContent = (
    <Grid container maxWidth="lg" sx={{ margin: '0 auto', padding: '0 1rem' }}>
      <Grid item xs={12} md={8}>
        <Grid container spacing={2}>
          {!isMobile && (
            <Grid item xs={12} sm={4}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: 1, color: 'text.primary', fontSize: '1rem' }}>
                ShieldShot LLC
              </Typography>
            </Grid>
          )}
          <Grid item xs={6} sm={isMobile ? 6 : 4}>
            <Box component="nav">
              <List dense sx={{ padding: 0 }}>
                <ListItem sx={{ padding: '2px 0' }}>
                  <Link to="/getting-started" style={{ textDecoration: 'none', color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                    Getting Started
                  </Link>
                </ListItem>
                <ListItem sx={{ padding: '2px 0' }}>
                  <Link to="/subscriptions" style={{ textDecoration: 'none', color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                    Subscriptions
                  </Link>
                </ListItem>
                <ListItem sx={{ padding: '2px 0' }}>
                  <Typography 
                    onClick={openTerms}
                    sx={{ textDecoration: 'none', color: 'text.secondary', fontSize: '0.875rem', cursor: 'pointer' }}
                  >
                    Terms of Service
                  </Typography>
                </ListItem>
              </List>
            </Box>
          </Grid>
          <Grid item xs={6} sm={isMobile ? 6 : 4}>
            <Box component="nav">
              <List dense sx={{ padding: 0 }}>
                <ListItem sx={{ padding: '2px 0' }}>
                  <Link to="/feedback" style={{ textDecoration: 'none', color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                    Send Feedback
                  </Link>
                </ListItem>
                <ListItem sx={{ padding: '2px 0' }}>
                  <Link to="/faq" style={{ textDecoration: 'none', color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                    FAQ
                  </Link>
                </ListItem>
                <ListItem sx={{ padding: '2px 0' }}>
                  <Link to="/updates" style={{ textDecoration: 'none', color: theme.palette.text.secondary, fontSize: '0.875rem' }}>
                    Changes and Updates
                  </Link>
                </ListItem>
              </List>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'center', md: 'right' }, marginTop: { xs: 1, md: 0 } }}>
        <Box sx={{ marginBottom: 1 }}>
          <IconButton 
            component="a"
            href="https://www.facebook.com/shieldshot.llc/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="ShieldShot on Facebook"
            sx={{ color: '#1877f2', marginRight: 1, padding: '4px' }}
            size="small"
          >
            <FontAwesomeIcon icon={faFacebook} />
          </IconButton>
          <IconButton 
            component="a"
            href="https://twitter.com/shieldshot_llc"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="ShieldShot on Twitter"
            sx={{ color: '#1da1f2', padding: '4px' }}
            size="small"
          >
            <FontAwesomeIcon icon={faTwitter} />
          </IconButton>
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ marginTop: 1, paddingTop: 1, borderTop: 1, borderColor: 'divider' }}>
        <Grid container>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              3101 N. Central Avenue Suite 183 #357 Phoenix, Arizona 85012
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'center', md: 'right' }, marginTop: { xs: 0.5, md: 0 } }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              © Copyright all rights reserved
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );

  if (!props.showFooter) return "";

  if (isMobile) {
    return (
      <>
        {/* Mobile Footer - Collapsible */}
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'background.default',
            borderTop: 1,
            borderColor: 'divider',
            zIndex: 1000,
            transform: expanded ? 'none' : 'translateY(calc(100% - 56px))',
            transition: 'transform 0.3s ease-in-out',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Header bar */}
          <Box
            onClick={toggleExpanded}
            sx={{
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderBottom: expanded ? 1 : 0,
              borderColor: 'divider',
              backgroundColor: expanded ? 'action.hover' : 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover'
              },
              transition: 'background-color 0.2s ease'
            }}
          >
            <Typography variant="subtitle1" sx={{ mr: 1, fontWeight: 'bold', color: 'text.primary' }}>
              ShieldShot LLC
            </Typography>
            {expanded ? <ExpandMore /> : <ExpandLess />}
          </Box>
          
          {/* Expandable content */}
          <Collapse in={expanded}>
            <Box sx={{ maxHeight: '60vh', overflow: 'auto', p: 2 }}>
              {desktopFooterContent}
            </Box>
          </Collapse>
        </Box>

        {/* Scroll to top button */}
        {showScrollTop && (
          <Fab
            size="small"
            onClick={scrollToTop}
            sx={{
              position: 'fixed',
              bottom: expanded ? 'auto' : 80,
              top: expanded ? 20 : 'auto',
              right: 16,
              zIndex: 1001,
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'scale(1.1)'
              },
              transition: 'background-color 0.2s ease, transform 0.2s ease'
            }}
          >
            <KeyboardArrowUpIcon />
          </Fab>
        )}

        {/* Terms Dialog */}
        <Dialog open={showTerms} onClose={closeTerms} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle>
            Terms of Service
            <IconButton
              onClick={closeTerms}
              aria-label="close"
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Terms />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Desktop Footer - Original styling
  return (
    <>
      <Dialog open={showTerms} onClose={closeTerms} maxWidth="md">
        <DialogTitle>
            <IconButton
                aria-label="close"
                onClick={closeTerms}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent>
            <Terms />
        </DialogContent>
      </Dialog>

      <Box 
        component="footer" 
        sx={{
          backgroundColor: 'background.default',
          padding: '1rem 0',
          marginTop: 'auto',
          borderTop: 1,
          borderColor: 'divider',
          position: 'relative',
          zIndex: 1
        }}
      >
        {desktopFooterContent}
      </Box>
    </>
  );
};

export default Footer;
